<i18n src="./resources/locales.json"></i18n>

<template>
  <v-container>
    <v-col>
      <div
        v-show="showUploadSection"
        id="upload_file_section"
        @dragleave="preventDefault"
        @dragover="preventDefault"
        @dragenter="preventDefault"
        @drop="handleChange"
      >
        <v-row justify="center" class="ma-0">
          <div id="drag_drop_area">
            <v-row justify="center">
              <img
                v-EcsImageHandler
                class="d-flex"
                :class="{ 'drag-svg-img': $vuetify.theme.dark }"
                src="./../../assets/drop-icon.svg"
                alt="Drop Area"
              />
            </v-row>
            <v-row justify="center" class="pa-2">
              <div v-once class="d-flex xtium-subtitle">{{ $t('image.dragHere') }}</div>
            </v-row>
          </div>
        </v-row>
        <v-row justify="center" class="pa-2">
          <h4 v-once>{{ $t('image.or') }}</h4>
        </v-row>
        <v-row justify="center" class="pa-2">
          <v-btn color="primary" class="custom-btn" @click="$refs.fileInput.click()">
            <span>{{ $t('image.selectFromComputer') }}</span>
          </v-btn>
        </v-row>
        <!-- Hidden File Input -->
        <input ref="fileInput" type="file" hidden @change="handleChange" />
      </div>

      <div v-if="showResizeSection" class="image-resize-section">
        <v-row class="pa-6" align="center" justify="center">
          <div class="mr-6">
            <div class="source-image-container">
              <img
                ref="img"
                v-EcsImageHandler
                :src="sourceImageUrl"
                :style="sourceImageStyle"
                class="source-image"
                draggable="false"
                alt="Profile Photo"
                @drag="preventDefault"
                @dragstart="preventDefault"
                @dragend="preventDefault"
                @dragleave="preventDefault"
                @dragover="preventDefault"
                @dragenter="preventDefault"
                @drop="preventDefault"
                @mousedown="imageStartMove"
                @mousemove="imageMove"
                @mouseup="createImg"
                @mouseout="createImg"
              />
              <div class="image-shade image-shade-1" :style="sourceImageShadeStyle" />
              <div class="image-shade image-shade-2" :style="sourceImageShadeStyle" />
            </div>

            <v-row align="center" class="zoom-controls ma-0">
              <v-btn text icon @mousedown="startZoomOut" @mouseup="scale.zoomOutOn = false">
                <v-icon>mdi-magnify-minus-outline</v-icon>
              </v-btn>

              <v-slider ref="zoomSlider" v-model="scale.range" hide-details="auto" @input="zoomChange" />

              <v-btn text icon @mousedown="startZoomIn" @mouseup="scale.zoomInOn = false">
                <v-icon>mdi-magnify-plus-outline</v-icon>
              </v-btn>
            </v-row>
          </div>

          <div class="ml-6">
            <div class="image-preview">
              <div class="image-preview-item">
                <img v-EcsImageHandler :src="finalImageUrl" :style="previewStyle" alt="Preview" />
                <span v-once>{{ $t('image.preview') }}</span>
              </div>
            </div>
            <v-btn color="primary" class="custom-btn" @click="$refs.fileInput.click()">
              {{ $t('buttons.change') }}
            </v-btn>
          </div>
        </v-row>
      </div>

      <canvas ref="canvas" :width="width" :height="height" hidden />
    </v-col>
  </v-container>
</template>

<script>
export default {
  name: 'EcsImageUpload',

  props: {
    allowedFileFormats: {
      type: Array,
      default() {
        return ['png', 'jpg'];
      }
    },
    height: {
      type: Number,
      default: 200
    },
    maxSize: {
      type: Number,
      default: 10240
    },
    width: {
      type: Number,
      default: 200
    }
  },

  data() {
    return {
      file: null,
      finalImageUrl: '',
      previewContainer: {
        width: 80,
        height: 80
      },
      step: 1,
      sourceImage: null,
      sourceImageContainer: {
        width: 300,
        height: 240
      },
      sourceImageMouseDown: {
        on: false,
        mX: 0,
        mY: 0,
        x: 0,
        y: 0
      },
      sourceImageUrl: '',
      scale: {
        zoomInOn: false,
        zoomOutOn: false,
        range: 1,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        maxWidth: 0,
        maxHeight: 0,
        minWidth: 0,
        minHeight: 0,
        naturalWidth: 0,
        naturalHeight: 0
      }
    };
  },

  computed: {
    previewStyle() {
      const { ratio, previewContainer } = this;
      const pc = previewContainer;
      let w = pc.width;
      let h = pc.height;
      const pcRatio = w / h;

      if (ratio < pcRatio) {
        w = pc.height * ratio;
      }
      if (ratio > pcRatio) {
        h = pc.width / ratio;
      }
      return {
        width: `${w}px`,
        height: `${h}px`
      };
    },
    ratio() {
      return this.width / this.height;
    },
    sourceImageMasking() {
      const { width, height, ratio, sourceImageContainer } = this;
      const sic = sourceImageContainer;
      const sicRatio = sic.width / sic.height;
      let x = 0;
      let y = 0;
      let w = sic.width;
      let h = sic.height;
      let scale = 1;
      if (ratio < sicRatio) {
        scale = sic.height / height;
        w = sic.height * ratio;
        x = (sic.width - w) / 2;
      }
      if (ratio > sicRatio) {
        scale = sic.width / width;
        h = sic.width / ratio;
        y = (sic.height - h) / 2;
      }
      return {
        scale,
        x,
        y,
        width: w,
        height: h
      };
    },
    sourceImageShadeStyle() {
      const { sourceImageMasking, sourceImageContainer } = this;
      const sic = sourceImageContainer;
      const sim = sourceImageMasking;
      const w = sim.width === sic.width ? sim.width : (sic.width - sim.width) / 2;
      const h = sim.height === sic.height ? sim.height : (sic.height - sim.height) / 2;

      return {
        width: `${w}px`,
        height: `${h}px`
      };
    },
    sourceImageStyle() {
      const { scale, sourceImageMasking } = this;
      const top = `${scale.y + sourceImageMasking.y}px`;
      const left = `${scale.x + sourceImageMasking.x}px`;
      return {
        top,
        left,
        width: `${scale.width}px`,
        height: `${scale.height}px`
      };
    },
    showResizeSection() {
      return this.step === 2;
    },
    showUploadSection() {
      return this.step === 1;
    }
  },

  methods: {
    checkFile(file) {
      if (!file) {
        return false;
      }

      if (file.type.indexOf('image') === -1) {
        return false;
      }

      return file.size / 1024 <= this.maxSize;
    },
    createImg(event) {
      const {
        file,
        imgBgc,
        sourceImage,
        scale: { x, y, width, height },
        sourceImageMasking: { scale }
      } = this;

      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');

      if (event) {
        this.sourceImageMouseDown.on = false;
      }

      canvas.width = this.width;
      canvas.height = this.height;
      ctx.clearRect(0, 0, this.width, this.height);

      if (file.type.indexOf('.png') >= 0) {
        ctx.fillStyle = 'rgba(0,0,0,0)';
      } else {
        ctx.fillStyle = imgBgc;
      }

      ctx.fillRect(0, 0, this.width, this.height);

      ctx.drawImage(sourceImage, x / scale, y / scale, width / scale, height / scale);

      this.finalImageUrl = canvas.toDataURL(file.type);
    },
    handleChange(event) {
      event.preventDefault();

      const files = event.target.files || event.dataTransfer.files;
      if (this.checkFile(files[0])) {
        this.setSourceImg(files[0]);
      }
    },
    imageMove(event) {
      event.preventDefault();
      const {
        sourceImageMouseDown: { on, mX, mY, x, y },
        scale,
        sourceImageMasking
      } = this;
      const sim = sourceImageMasking;
      const nX = event.screenX;
      const nY = event.screenY;
      const dX = nX - mX;
      const dY = nY - mY;
      let rX = x + dX;
      let rY = y + dY;
      if (!on) {
        return;
      }
      if (rX > 0) {
        rX = 0;
      }
      if (rY > 0) {
        rY = 0;
      }
      if (rX < sim.width - scale.width) {
        rX = sim.width - scale.width;
      }
      if (rY < sim.height - scale.height) {
        rY = sim.height - scale.height;
      }
      scale.x = rX;
      scale.y = rY;
    },
    imageStartMove(e) {
      e.preventDefault();
      const { sourceImageMouseDown, scale } = this;
      sourceImageMouseDown.mX = event.screenX;
      sourceImageMouseDown.mY = event.screenY;
      sourceImageMouseDown.x = scale.x;
      sourceImageMouseDown.y = scale.y;
      sourceImageMouseDown.on = true;
    },
    preventDefault(event) {
      event.preventDefault();
      return false;
    },
    reset() {
      this.setStep(1);
      this.file = null;
      this.$refs.fileInput.value = null;
      this.$emit('selected-file', this.file);
    },
    setSourceImg(file) {
      this.file = file;
      this.$emit('src-file-set', file.name, file.type, file.size);
      const that = this;
      const fr = new FileReader();
      fr.readAsDataURL(file);
      fr.onload = function() {
        that.sourceImageUrl = fr.result;
        that.startCrop();
      };
    },
    setStep(step) {
      setTimeout(() => {
        this.step = step;
      }, 200);
    },
    startCrop() {
      const that = this;
      const { ratio, scale, sourceImageUrl, sourceImageMasking } = that;
      const sim = sourceImageMasking;
      const img = new Image();
      img.src = sourceImageUrl;
      img.onload = function() {
        const nWidth = img.naturalWidth;
        const nHeight = img.naturalHeight;
        const nRatio = nWidth / nHeight;
        let w = sim.width;
        let h = sim.height;
        let x = 0;
        let y = 0;

        if (ratio > nRatio) {
          h = w / nRatio;
          y = (sim.height - h) / 2;
        }

        if (ratio < nRatio) {
          w = h * nRatio;
          x = (sim.width - w) / 2;
        }

        scale.range = 0;
        scale.x = x;
        scale.y = y;
        scale.width = w;
        scale.height = h;
        scale.minWidth = w;
        scale.minHeight = h;
        scale.maxWidth = nWidth * sim.scale;
        scale.maxHeight = nHeight * sim.scale;
        scale.naturalWidth = nWidth;
        scale.naturalHeight = nHeight;
        that.sourceImage = img;
        that.createImg();
        that.setStep(2);
        that.$emit('selected-file', that.file);
      };
    },
    startZoomIn() {
      const that = this;
      const { scale } = that;
      scale.zoomInOn = true;

      function zoom() {
        if (scale.zoomInOn) {
          let range;
          if (scale.range >= 100) {
            range = 100;
          } else {
            ++scale.range;
            range = scale.range;
          }
          that.zoomImg(range);
          setTimeout(() => {
            zoom();
          }, 60);
        }
      }
      zoom();
    },
    startZoomOut() {
      const that = this;
      const { scale } = that;
      scale.zoomOutOn = true;

      function zoom() {
        if (scale.zoomOutOn) {
          let range;
          if (scale.range <= 0) {
            range = 0;
          } else {
            --scale.range;
            range = scale.range;
          }
          that.zoomImg(range);
          setTimeout(() => {
            zoom();
          }, 60);
        }
      }
      zoom();
    },
    zoomChange() {
      this.zoomImg(this.scale.range);
    },
    zoomImg(newRange) {
      const that = this;
      const { sourceImageMasking, scale } = this;
      const { maxWidth, maxHeight, minWidth, minHeight, width, height, x, y } = scale;
      const sim = sourceImageMasking;

      const sWidth = sim.width;
      const sHeight = sim.height;

      const nWidth = minWidth + ((maxWidth - minWidth) * newRange) / 100;
      const nHeight = minHeight + ((maxHeight - minHeight) * newRange) / 100;

      let nX = sWidth / 2 - (nWidth / width) * (sWidth / 2 - x);
      let nY = sHeight / 2 - (nHeight / height) * (sHeight / 2 - y);

      if (nX > 0) {
        nX = 0;
      }
      if (nY > 0) {
        nY = 0;
      }
      if (nX < sWidth - nWidth) {
        nX = sWidth - nWidth;
      }
      if (nY < sHeight - nHeight) {
        nY = sHeight - nHeight;
      }

      scale.x = nX;
      scale.y = nY;
      scale.width = nWidth;
      scale.height = nHeight;
      scale.range = newRange;
      setTimeout(function() {
        if (scale.range === newRange) {
          that.createImg();
        }
      }, 300);
    }
  }
};
</script>

<style scoped>
.custom-btn {
  font-size: 12px;
  text-transform: Uppercase;
}
.source-image-container {
  position: relative;
  display: block;
  width: 300px;
  height: 240px;
  background-color: #e5e5e0;
  overflow: hidden;
}
.source-image-container .source-image {
  position: absolute;
  display: block;
  cursor: move;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.image-shade {
  -webkit-box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  position: absolute;
  background-color: rgba(241, 242, 243, 0.8);
}
.image-shade.image-shade-1 {
  top: 0;
  left: 0;
}
.image-shade.image-shade-2 {
  bottom: 0;
  right: 0;
}
.zoom-controls {
  width: 300px;
}
.image-preview {
  height: 150px;
  overflow: hidden;
}
.image-preview-item {
  margin: auto;
}
.image-preview .image-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
}
.image-preview .image-preview-item span {
  position: absolute;
  bottom: -30px;
  width: 100%;
  font-size: 14px;
  color: #bbb;
  display: block;
  text-align: center;
}
.image-preview .image-preview-item img {
  overflow: hidden;
  border-radius: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.drag-svg-img {
  filter: invert(100%);
}
</style>
